export const FRESHNESS_THRESHOLDS_MS = Object.freeze({
  live: 30_000,
  recent: 2 * 60_000,
  lastConfirmed: 10 * 60_000,
  // A foreground client heartbeats well inside this, so an open app stays ACTIVE
  // even if its socket briefly drops.
  active: 2 * 60_000,
  // How long a device stays REACHABLE after the server last heard from it by any
  // route. Deliberately far longer than the old two-minute window: a backgrounded
  // phone contacts us on its own schedule, and a quiet gap is not evidence that
  // the phone is off.
  reachable: 15 * 60_000
});

/**
 * The two timestamps a location carries, and why neither is enough alone.
 *
 *   recordedAt  when the device says it observed the position. Client supplied.
 *               The server bounds it (no more than five minutes ahead, no more
 *               than seven days behind) but cannot verify it, so a fast device
 *               clock or a replayed cached fix can make it look current.
 *   createdAt   when the server wrote the row. Stamped server-side, trusted —
 *               but it only says when the report arrived, not when the position
 *               was true. An hour-old fix uploaded on wake has a createdAt of
 *               now and still describes where the phone was an hour ago.
 *
 * A position is therefore only as fresh as the older of the two.
 */
export type LocationEvidence = {
  recordedAt?: string | null;
  createdAt?: string | null;
};

/**
 * Location evidence only. STALE describes a coordinate, never a phone.
 */
export type FreshnessStatus = {
  label: 'LIVE' | 'RECENT' | 'LAST CONFIRMED' | 'STALE' | 'NO LOCATION';
  age: string;
  tone: 'live' | 'recent' | 'confirmed' | 'stale' | 'offline';
};

/**
 * Whether the server can still reach the device. Separate from where it is.
 *
 *   ONLINE       an app session is live right now — socket connected, or a
 *                foreground heartbeat within the active window.
 *   REACHABLE    no session, but the device contacted us recently from the
 *                background or answered a push. Find Now still works.
 *   UNREACHABLE  nothing heard for longer than the window.
 */
export type ReachabilityState = 'ONLINE' | 'REACHABLE' | 'UNREACHABLE';
export type Reachability = {
  state: ReachabilityState;
  label: 'ACTIVE' | 'IN BACKGROUND' | 'UNREACHABLE';
  age: string;
  via?: string | null;
};

export function formatAge(timestamp?: string | null, now = Date.now()) {
  if (!timestamp) return 'Never';
  const seconds = Math.max(0, Math.floor((now - new Date(timestamp).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

/**
 * The instant a position may claim to be true, given both timestamps.
 *
 * min(observed, received): a point can only be called fresher when the device's
 * own claim and the server's receipt both support it. That covers the two ways
 * freshness used to be fabricated — a device asserting a time it was not
 * observed at, and an old observation arriving late — with one rule, and it
 * absorbs bounded clock skew for free, because an observation stamped in the
 * future can never be older than the receipt that follows it.
 *
 * Falls back to whichever timestamp exists when only one does, so a payload
 * from an endpoint that does not project createdAt keeps its previous meaning
 * rather than silently reading as NO LOCATION.
 */
export function effectiveFreshnessAt(point?: LocationEvidence | null): string | null {
  const observed = point?.recordedAt ? new Date(point.recordedAt).getTime() : NaN;
  const received = point?.createdAt ? new Date(point.createdAt).getTime() : NaN;
  const observedValid = Number.isFinite(observed);
  const receivedValid = Number.isFinite(received);
  if (!observedValid && !receivedValid) return null;
  if (!observedValid) return new Date(received).toISOString();
  if (!receivedValid) return new Date(observed).toISOString();
  return new Date(Math.min(observed, received)).toISOString();
}

export function locationFreshness(point?: LocationEvidence | null, now = Date.now()): FreshnessStatus {
  const effectiveAt = effectiveFreshnessAt(point);
  if (!effectiveAt) return { label: 'NO LOCATION', age: 'No confirmed position', tone: 'offline' };
  const ageMs = Math.max(0, now - new Date(effectiveAt).getTime());
  // Reported from the same instant the label is earned from, so the wording and
  // the age can never describe two different moments.
  const age = formatAge(effectiveAt, now);
  if (ageMs <= FRESHNESS_THRESHOLDS_MS.live) return { label: 'LIVE', age, tone: 'live' };
  if (ageMs <= FRESHNESS_THRESHOLDS_MS.recent) return { label: 'RECENT', age, tone: 'recent' };
  if (ageMs <= FRESHNESS_THRESHOLDS_MS.lastConfirmed) return { label: 'LAST CONFIRMED', age, tone: 'confirmed' };
  return { label: 'STALE', age, tone: 'stale' };
}

export function deviceReachability(
  lastSeenAt?: string | null,
  socketConnected?: boolean,
  now = Date.now(),
  via?: string | null
): Reachability {
  const age = lastSeenAt ? formatAge(lastSeenAt, now) : 'Never seen';
  const contacted = lastSeenAt ? now - new Date(lastSeenAt).getTime() : Infinity;
  // An open app is ACTIVE whether we learn it from the socket or from the
  // foreground heartbeat that backs it up.
  if (socketConnected || (via === 'FOREGROUND' && contacted <= FRESHNESS_THRESHOLDS_MS.active)) {
    return { state: 'ONLINE', label: 'ACTIVE', age, via: via ?? 'SOCKET' };
  }
  if (contacted <= FRESHNESS_THRESHOLDS_MS.reachable) {
    return { state: 'REACHABLE', label: 'IN BACKGROUND', age, via };
  }
  return { state: 'UNREACHABLE', label: 'UNREACHABLE', age, via };
}

/**
 * The headline status for a device's *location*.
 *
 * Location freshness leads and stands alone: a confirmed position stays true
 * whether or not the app holds a socket. Reachability is reported separately and
 * must never be folded in here.
 */
export function deviceStatus(_lastSeenAt?: string | null, point?: LocationEvidence | null, now = Date.now()): FreshnessStatus {
  return locationFreshness(point, now);
}

export function devicePresence(
  lastSeenAt?: string | null,
  now = Date.now(),
  socketConnected?: boolean,
  via?: string | null
) {
  const reachability = deviceReachability(lastSeenAt, socketConnected, now, via);
  return {
    online: reachability.state !== 'UNREACHABLE',
    active: reachability.state === 'ONLINE',
    label: reachability.label,
    age: reachability.age,
    state: reachability.state
  };
}
